import { Box, Flex, Heading, Skeleton } from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { SectionHeaderActions } from 'components/sections/header/actions';
import { Breadcrumbs } from 'components/sections/header/breadcrumbs';
import { SubSectionSelector } from 'components/sections/header/subsection-selector';
import { DefaultTFuncReturn } from 'i18next';
import { IBreadcrumb } from 'interfaces/i-breadcrumbs';
import { IMenuAction } from 'interfaces/i-menus';
import { ISubSection } from 'interfaces/i-section';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { ReactNode } from 'react';

interface IProps {
  /** header text, won't be translated */
  header?: DefaultTFuncReturn | string;
  headerNode?: ReactNode;

  badge1?: ReactNode;
  badge2?: ReactNode;

  subsections?: ISubSection[];

  /** optional, will be rendered on the right side opposite the header as a standalone button */
  mainAction?: IMenuAction;

  // e.g. for import method + normalize release controls
  secondaryAction?: ReactNode;

  /** optional, will be rendered on the right side opposite the header as a menu */
  actions?: IMenuAction[];

  // if provided, actions will be forced to render as a menu
  actionsMenuTrigger?: ReactNode;

  // e.g. for hitter library actions
  extendedActionsMenu?: boolean;

  /** todo: deprecate this if not needed */
  loading?: boolean;

  breadcrumbs?: IBreadcrumb[];

  onClick?: () => void;
}

const TextHeader = (props: {
  loading: boolean;
  header?: DefaultTFuncReturn | string;
}) => {
  return (
    <Skeleton loading={!!props.loading}>
      <Heading>{props.header}</Heading>
    </Skeleton>
  );
};

export const SectionHeader = (props: IProps) => {
  return (
    <ErrorBoundary identifier="SectionHeader">
      <Flex
        direction="column"
        gap={props.loading ? RADIX.FLEX.GAP.SM : RADIX.FLEX.GAP.XS}
      >
        {props.breadcrumbs && (
          <Breadcrumbs items={props.breadcrumbs} loading={props.loading} />
        )}

        <Flex
          gap={RADIX.FLEX.GAP.SM}
          justify="between"
          className={props.onClick ? 'cursor-pointer' : undefined}
          onClick={props.onClick}
          wrap="wrap"
          align="center"
        >
          {/* left cluster */}
          <Flex gap={RADIX.FLEX.GAP.SM} align="center">
            {props.headerNode || (
              <TextHeader loading={!!props.loading} header={props.header} />
            )}

            {props.subsections && (
              <SubSectionSelector subsections={props.subsections} />
            )}

            {props.badge1}
            {props.badge2}
          </Flex>

          {/* right cluster */}
          <Flex gap={RADIX.FLEX.GAP.SM}>
            {props.secondaryAction && <Box>{props.secondaryAction}</Box>}

            {props.mainAction && !props.mainAction.invisible && (
              <SectionHeaderActions actions={[props.mainAction]} />
            )}

            {props.actions && (
              <SectionHeaderActions
                actions={props.actions}
                customTrigger={props.actionsMenuTrigger}
                extended={props.extendedActionsMenu}
              />
            )}
          </Flex>
        </Flex>
      </Flex>
    </ErrorBoundary>
  );
};
